import { CARD_COMPARISON_LIMITS, SESSION_CONSTANTS, StringBoolean } from '../../constants';

type QueryValue = string | string[] | undefined;
type QueryLike = Record<string, QueryValue>;

const OWNED_CARDTYPE_PARAMS = ['cardtype1', 'cardtype2', 'cardtype3'];
const OWNED_CARDTYPE_PARAM_SET = new Set(OWNED_CARDTYPE_PARAMS);
const COMPARISON_SLOT_PARAM_LIMIT = OWNED_CARDTYPE_PARAMS.length;

export type CompareSelectionCard = {
  cardname?: string;
  checked?: boolean;
};

export type ComparisonDropdownOption = {
  value: string;
  label: string;
  imagePath: string;
};

export type Card = {
  cardId: string;
  cardName: string;
  imagePath: string;
};

type BuildCompareUrlArgs = {
  baseUrl?: string;
  cardNames: string[];
  routerQuery?: Record<string, QueryValue>;
  targetExperience?: string;
};

type BuildSelectedCardsCompareUrlArgs = Omit<BuildCompareUrlArgs, 'cardNames'> & {
  selectedCards: CompareSelectionCard[];
};

type PersistCompareSelectionsArgs = {
  cobrandData: unknown;
  currentPath?: string;
};

type RestoreCompareSelectionsArgs<T> = {
  setCobrandData: (data: T) => void;
};

type CompareClickTrackArgs = {
  linkType: 'internal' | 'external';
  trackingProperties?: {
    trackingContentPosition?: string;
    trackingDescription?: string;
    additionalTrackingVariables?: string;
  };
};

type BuildComparisonSlotUrlArgs = {
  asPath?: string;
  pathname?: string;
  selectedCardIds: string[];
};

export const toBoolean = (value?: StringBoolean | string | boolean): boolean =>
  value === true || value === 'true' || value === StringBoolean.TRUE;

export const getMaxCompareCards = (isDesktop: boolean): number =>
  isDesktop ? CARD_COMPARISON_LIMITS.MAX_CARDS_DESKTOP : CARD_COMPARISON_LIMITS.MAX_CARDS_MOBILE;

const normalizeQueryValue = (value: QueryValue): string | undefined => {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
};

export const isFromFOCComparisonEntry = (fromFOC?: QueryValue | boolean): boolean => {
  const normalizedFromFOC = Array.isArray(fromFOC) ? fromFOC[0] : fromFOC;
  return normalizedFromFOC === true || normalizedFromFOC === 'true';
};

export const parseDefaultCardIds = (value?: string | string[]): string[] => {
  if (!value) {
    return [];
  }

  const rawValues = Array.isArray(value) ? value : value.split(',');
  return rawValues.map(item => item.trim()).filter(Boolean);
};

type GetResolvedDefaultComparisonCardIdsArgs = {
  pageItems?: Record<string, unknown>;
  defaultDesktopCardCodes?: string | string[];
  defaultMobileCardCodes?: string | string[];
  slotLimit: number;
};

export const getResolvedDefaultComparisonCardIds = ({
  pageItems,
  defaultDesktopCardCodes,
  defaultMobileCardCodes,
  slotLimit,
}: GetResolvedDefaultComparisonCardIdsArgs): string[] => {
  const desktopIds = parseDefaultCardIds(defaultDesktopCardCodes);
  const mobileIds = parseDefaultCardIds(defaultMobileCardCodes);
  const configuredIds =
    slotLimit >= CARD_COMPARISON_LIMITS.MAX_CARDS_DESKTOP
      ? desktopIds
      : mobileIds.length > 0
      ? mobileIds
      : desktopIds.slice(0, CARD_COMPARISON_LIMITS.MAX_CARDS_MOBILE);

  const validPageItemIds = new Set(Object.keys(pageItems ?? {}).filter(cardId => cardId !== 'categories'));

  return compactComparisonCardIds(
    configuredIds.filter(cardId => validPageItemIds.has(cardId)),
    slotLimit
  );
};

export const compactComparisonCardIds = (cardIds: string[] = [], slotLimit = COMPARISON_SLOT_PARAM_LIMIT): string[] =>
  cardIds.filter(Boolean).slice(0, slotLimit);

export const getComparisonCardIdsFromQuery = (
  query: QueryLike = {},
  slotLimit = COMPARISON_SLOT_PARAM_LIMIT
): string[] =>
  compactComparisonCardIds(
    OWNED_CARDTYPE_PARAMS.map(param => normalizeQueryValue(query[param])).filter(Boolean) as string[],
    slotLimit
  );

export const getComparisonCardIdsFromUrl = (
  asPath = '',
  fallbackQuery: QueryLike = {},
  slotLimit = COMPARISON_SLOT_PARAM_LIMIT
): string[] => {
  const [pathWithoutHash] = asPath.split('#');

  if (!pathWithoutHash.includes('?')) {
    return getComparisonCardIdsFromQuery(fallbackQuery, slotLimit);
  }

  const [, rawQueryString = ''] = pathWithoutHash.split('?');
  const searchParams = new URLSearchParams(rawQueryString);
  const queryFromUrl = OWNED_CARDTYPE_PARAMS.reduce<QueryLike>((acc, param) => {
    const value = searchParams.get(param);
    if (value) {
      acc[param] = value;
    }
    return acc;
  }, {});

  return getComparisonCardIdsFromQuery(queryFromUrl, slotLimit);
};

export const buildComparisonSlots = (
  cardIds: string[] = [],
  slotCount = COMPARISON_SLOT_PARAM_LIMIT
): (string | undefined)[] => {
  const compactedCardIds = compactComparisonCardIds(cardIds, slotCount);
  return Array.from({ length: slotCount }, (_, index) => compactedCardIds[index]);
};

export const clearComparisonSlot = (
  cardIds: string[] = [],
  slotIndex: number,
  slotLimit = COMPARISON_SLOT_PARAM_LIMIT
): string[] =>
  compactComparisonCardIds(
    cardIds.filter((_, index) => index !== slotIndex),
    slotLimit
  );

export const selectComparisonCard = (
  cardIds: string[] = [],
  selectedCardId: string,
  slotLimit = COMPARISON_SLOT_PARAM_LIMIT
): string[] => {
  if (!selectedCardId) return compactComparisonCardIds(cardIds, slotLimit);

  const compactedCardIds = compactComparisonCardIds(cardIds, slotLimit).filter(cardId => cardId !== selectedCardId);
  if (compactedCardIds.length >= slotLimit) return compactedCardIds;

  return [...compactedCardIds, selectedCardId];
};

export const buildComparisonSlotUrl = ({
  asPath = '',
  pathname = '',
  selectedCardIds,
}: BuildComparisonSlotUrlArgs): string => {
  const [pathWithoutHash, hash] = asPath.split('#');
  const [pathFromAsPath, rawQueryString = ''] = pathWithoutHash.split('?');
  const basePath = pathFromAsPath || pathname;
  const preservedQueryPairs = rawQueryString
    .split('&')
    .filter(Boolean)
    .filter(pair => {
      const key = pair.split('=')[0];
      return !OWNED_CARDTYPE_PARAM_SET.has(key);
    });
  const cardTypePairs = compactComparisonCardIds(selectedCardIds, COMPARISON_SLOT_PARAM_LIMIT).map(
    (cardId, index) => `cardtype${index + 1}=${encodeURIComponent(cardId)}`
  );
  const queryString = [...cardTypePairs, ...preservedQueryPairs].join('&');
  const hashString = hash ? `#${hash}` : '';

  return `${basePath}${queryString ? `?${queryString}` : ''}${hashString}`;
};

export const getComparisonDropdownOptions = (
  allCardsList: Card[] = [],
  selectedCardIds: string[] = []
  /* eslint-disable @typescript-eslint/no-explicit-any */
): any[] => {
  const selectedCardIdSet = new Set(selectedCardIds);
  return allCardsList
    .filter(card => !selectedCardIdSet.has(card.cardId))
    .map(card => ({
      value: card.cardId,
      label: card.cardName,
      imagePath: card.imagePath,
    }));
};

export type CobrandCard = {
  cardCode: string;
  cardHeader: string;
  imagePathWithoutBadge: string;
  cardName?: string;
  cardType?: string;
  imagePath?: string;
};

export const buildAllCardsListFromCobrandCards = (cobrandCards: Record<string, CobrandCard> = {}): Card[] =>
  Object.entries(cobrandCards)
    .map(([key, entry]) => {
      // Support legacy/new cobrand shapes: either entry.cardCode/cardHeader/imagePathWithoutBadge
      // or an AEM-style shape with nested card_data containing cardSummary/html
      if (!entry) return null;

      // Skip category grouping entries
      if (key === 'categories') return null;

      // If explicit cardCode exists, prefer it
      if ((entry as any).cardCode) {
        return {
          cardId: (entry as any).cardCode,
          cardName: (entry as any).cardHeader || (entry as any).cardName || '',
          imagePath: (entry as any).imagePathWithoutBadge || (entry as any).imagePath || '',
        } as Card;
      }

      // Otherwise, attempt to infer from nested card_data or use the object key as id
      const nested = (entry as any).card_data || (entry as any).cardData || entry;
      const rawName = nested?.cardSummary || nested?.cardHeader || nested?.cardName || '';

      // Strip simple HTML tags from summary if present
      const cardName = String(rawName)
        .replace(/<[^>]*>/g, '')
        .trim();

      return {
        cardId: key,
        cardName,
        imagePath: nested?.imagePath || nested?.imagePathWithoutBadge || '',
      } as Card;
    })
    .filter(Boolean) as Card[];

export const buildCompareUrlFromCardNames = ({
  baseUrl = '',
  cardNames,
  routerQuery,
  targetExperience,
}: BuildCompareUrlArgs): string => {
  const [baseUrlWithoutHash, hash] = baseUrl.split('#');
  const [basePath, baseQueryString = ''] = baseUrlWithoutHash.split('?');
  const params = new URLSearchParams(baseQueryString);

  cardNames.filter(Boolean).forEach((cardName, index) => {
    params.set(`cardtype${index + 1}`, cardName);
  });

  if (targetExperience) {
    params.set('experience', targetExperience);
  }

  if (routerQuery) {
    Object.entries(routerQuery).forEach(([key, value]) => {
      if (key === 'cardName') return;

      const normalizedValue = normalizeQueryValue(value);
      if (normalizedValue) {
        params.set(key, normalizedValue);
      }
    });
  }

  const queryString = params.toString();
  const hashString = hash ? `#${hash}` : '';

  return `${basePath}${queryString ? `?${queryString}` : ''}${hashString}`;
};

export const buildSelectedCardsCompareUrl = ({ selectedCards, ...rest }: BuildSelectedCardsCompareUrlArgs): string =>
  buildCompareUrlFromCardNames({
    ...rest,
    cardNames: selectedCards
      .filter(card => card?.checked !== false)
      .map(card => card.cardname ?? '')
      .filter(Boolean),
  });

export const persistCompareSelections = ({ cobrandData, currentPath = '' }: PersistCompareSelectionsArgs): void => {
  if (typeof window === 'undefined') return;

  sessionStorage?.setItem(SESSION_CONSTANTS.COBRAND_DATA, JSON.stringify(cobrandData));
  sessionStorage?.setItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT, JSON.stringify(currentPath));
};

export const restoreCompareSelections = <T>({ setCobrandData }: RestoreCompareSelectionsArgs<T>): void => {
  if (typeof window === 'undefined') return;

  const cobrandDataFromSessionStorage = sessionStorage?.getItem(SESSION_CONSTANTS.COBRAND_DATA);

  if (cobrandDataFromSessionStorage) {
    try {
      setCobrandData(JSON.parse(cobrandDataFromSessionStorage) as T);
    } catch {
      // Ignore invalid persisted selections.
    } finally {
      sessionStorage.removeItem(SESSION_CONSTANTS.COBRAND_DATA);
    }
  }

  sessionStorage?.removeItem(SESSION_CONSTANTS.CREDIT_CARD_ENDPOINT);
};

const normalizeSelectedCardIds = (cards: CompareSelectionCard[] = []): string[] =>
  cards.map(card => card?.cardname ?? '');

export const hasSelectedCardsChanged = (
  previousSelectedCards: CompareSelectionCard[] = [],
  nextSelectedCards: CompareSelectionCard[] = []
): boolean => {
  if (previousSelectedCards.length !== nextSelectedCards.length) {
    return true;
  }

  const previousIds = normalizeSelectedCardIds(previousSelectedCards);
  const nextIds = normalizeSelectedCardIds(nextSelectedCards);

  return previousIds.some((cardId, index) => cardId !== nextIds[index]);
};

export const buildCompareClickTrackValue = ({ linkType, trackingProperties }: CompareClickTrackArgs): string => {
  const position = trackingProperties?.trackingContentPosition ?? '';
  const description = trackingProperties?.trackingDescription ?? '';
  const additional = trackingProperties?.additionalTrackingVariables ?? '';

  return `${position}|${description}|${linkType}|${additional}`;
};
